import type {
  LabBulkDecideInput,
  LabBulkDecideResponse,
  LabRemoteConfigResponse,
  LabSeedIdResponse,
} from '@luxuryescapes/contract-svc-lab'

import config from 'constants/config'
import request, { authOptions } from 'api/requestUtils'

export const FUNNEL_OPTIMISER_API_TIMEOUT_MS = 2500

const getLabEndpointOverride = (): string | undefined => {
  const labApiHost = config.LAB_API_HOST
  if (!labApiHost) {
    return undefined
  }

  if (labApiHost.startsWith('http://') || labApiHost.startsWith('https://')) {
    return labApiHost
  }

  return `${config.SCHEME}://${labApiHost}`
}

type HeaderBannerEvaluateInput = {
  distinctId: string
  currentPath: string
  brand: App.BrandName
  attributes?: Record<string, unknown>
  platform?: 'desktop' | 'mobileWeb' | 'app'
}

const filterContextAttributes = (attributes?: Record<string, unknown>) => {
  if (!attributes) {
    return undefined
  }

  const filteredAttributes = Object.fromEntries(
    Object.entries(attributes).filter(([_, value]) => value !== undefined && value !== ''),
  )

  return Object.keys(filteredAttributes).length > 0 ? filteredAttributes : undefined
}

const mergeAttributes = (
  baseAttributes?: Record<string, unknown>,
  additionalAttributes?: Record<string, unknown>,
) => {
  return filterContextAttributes({
    ...baseAttributes,
    ...additionalAttributes,
  })
}

const getFeatureFlagContext = (featureFlagContext: App.FeatureFlagContext | undefined) => {
  if (!featureFlagContext) {
    return undefined
  }

  // geoStateName is also sent via additionalAttributes (from getLabGeoAttributes in SystemActions).
  // When both are present the additionalAttributes value takes precedence in mergeAttributes,
  // which is intentional: the client-side geo value is the most up-to-date source.
  const geoStateName = featureFlagContext.geoStateName?.trim()

  const { utmCampaign, utmContent, utmMedium, utmSource, utmTerm, deviceName, regionCode } = featureFlagContext

  return filterContextAttributes({
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
    utmTerm,
    deviceName,
    regionCode,
    geoStateName,
  })
}

const getHeaderBannerPlatform = (
  featureFlagContext: App.FeatureFlagContext | undefined,
): 'desktop' | 'mobileWeb' => {
  const deviceName = featureFlagContext?.deviceName?.trim().toLowerCase()
  return deviceName === 'mobile' ? 'mobileWeb' : 'desktop'
}

export const getFeatureFlagDecisions = async(
  { keys, distinctId, featureFlagContext, token, additionalAttributes, forceRefresh, timeout }: {
    keys: Array<string>,
    distinctId: string,
    featureFlagContext?: App.FeatureFlagContext,
    token?: string,
    additionalAttributes?: Record<string, unknown>,
    forceRefresh?: boolean,
    timeout?: number,
  },
): Promise<Record<string, App.LabFeatureFlagResult>> => {
  const options = { ...authOptions(token), timeout }
  const baseAttributes = getFeatureFlagContext(featureFlagContext)
  const endpointOverride = getLabEndpointOverride()

  const input: LabBulkDecideInput = {
    keys,
    distinctId,
    attributes: mergeAttributes(baseAttributes, additionalAttributes),
    forceRefresh,
  }
  const response = await request.post<LabBulkDecideResponse, LabBulkDecideInput>('/api/lab/decide', input, options, endpointOverride)
  if (!response) return {} as Record<string, App.LabFeatureFlagResult>
  return response.data
}

// preview endpoint does not require authentication
export const getLabPreview = async(previewId: string): Promise<Record<string, App.LabFeatureFlagResult>> => {
  const response = await request.get<{ data: Record<string, App.LabFeatureFlagResult> }>(`/api/lab/preview/${previewId}`, {}, getLabEndpointOverride())
  if (!response) return {} as Record<string, App.LabFeatureFlagResult>
  return response.data
}

export const getSeedIdFromApi = async(memberId: string): Promise<string | undefined> => {
  const response = await request.get<LabSeedIdResponse>(`/api/lab/seed-id?memberId=${encodeURIComponent(memberId)}`, {}, getLabEndpointOverride())
  return response?.data.seedId ?? undefined
}

export const getRemoteConfigFlags = async(
  keys: Array<string>,
): Promise<Record<string, App.LabRemoteConfigResult>> => {
  const response = await request.post<LabRemoteConfigResponse, { keys: Array<string> }>('/api/lab/remote-config', { keys }, {}, getLabEndpointOverride())
  if (!response) return {} as Record<string, App.LabRemoteConfigResult>
  return response.data
}

export const getHeaderBanners = async(
  { distinctId, currentPath, featureFlagContext, token, additionalAttributes }: {
    distinctId: string,
    currentPath: string,
    featureFlagContext?: App.FeatureFlagContext,
    token?: string,
    additionalAttributes?: Record<string, unknown>,
  },
): Promise<App.LabHeaderBannersResult | undefined> => {
  const options = token ? authOptions(token) : {}
  const attributes = mergeAttributes(getFeatureFlagContext(featureFlagContext), additionalAttributes)
  const endpointOverride = getLabEndpointOverride()
  const input: HeaderBannerEvaluateInput = {
    distinctId,
    currentPath,
    brand: config.BRAND,
    attributes,
    platform: getHeaderBannerPlatform(featureFlagContext),
  }
  const response = await request.post<{ data: App.LabHeaderBannersResult }, HeaderBannerEvaluateInput>('/api/lab/header-banners', input, options, endpointOverride)
  return response?.data
}
